"use client";

import React from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Button from "@/components/ui/button/Button";
import SearchableSelect from "@/components/ui/SearchableSelect";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import Radio from "@/components/form/input/Radio";
import { Modal } from "@/components/ui/modal/index";
import { extractApiEnvelopeMessage, handleApiError } from "@/lib/api";
import { rmcClient } from "@/lib/rmcClient";
import { roundMoney, roundToDecimals } from "@/lib/utils";
import { useRateLookup } from "@/hooks/useRateLookup";

const rowSchema = z.object({
  vehicle_id: z.coerce.number().refine((n) => Number.isFinite(n) && n > 0, "Vehicle required"),
  driver_id: z.coerce.number().refine((n) => Number.isFinite(n) && n > 0, "Driver required"),
  grade_id: z.coerce.number().refine((n) => Number.isFinite(n) && n > 0, "Grade required"),
  batch_no: z.string().optional(),
  qty_cum: z.coerce.number().positive("Qty required"),
  rate: z.coerce.number().positive("Rate required"),
  amount: z.union([z.string(), z.number()]).optional(),
  remark: z.string().optional(),
});

const schema = z.object({
  party_id: z.coerce.number().refine((n) => Number.isFinite(n) && n > 0, "Party required"),
  date: z.string().min(1, "Date required"),
  site_id: z.coerce.number().refine((n) => Number.isFinite(n) && n > 0, "Site required"),
  order_by: z.string().optional(),
  rows: z.array(rowSchema).min(1, "Add at least one row"),
});

const emptyRow = {
  vehicle_id: "",
  driver_id: "",
  grade_id: "",
  batch_no: "",
  qty_cum: "",
  rate: "",
  amount: "",
  remark: "",
};

export default function BulkSalesChallanForm() {
  const qc = useQueryClient();
  const { fetchRateDetails } = useRateLookup();
  const [invoicePrompt, setInvoicePrompt] = React.useState(false);
  const [createdChallanIds, setCreatedChallanIds] = React.useState([]);
  const [invoiceTaxType, setInvoiceTaxType] = React.useState("GST");
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      party_id: "",
      date: new Date().toISOString().slice(0, 10),
      site_id: "",
      order_by: "",
      rows: [{ ...emptyRow }],
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: "rows" });
  const partyId = watch("party_id");
  const rows = watch("rows");

  const { data: parties = [] } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });
  const { data: grades = [] } = useQuery({
    queryKey: ["grades", partyId],
    queryFn: async () => (await rmcClient.listGrades({ party_id: partyId })).data,
    enabled: !!partyId,
  });
  const { data: vehicles = [] } = useQuery({
    queryKey: ["vehicles"],
    queryFn: async () => (await rmcClient.listVehicles()).data,
  });
  const { data: drivers = [] } = useQuery({
    queryKey: ["drivers", "bulkSalesChallanForm"],
    queryFn: async () => (await rmcClient.listDrivers()).data,
  });
  const driverOptions = React.useMemo(
    () => drivers.filter((d) => d.is_active !== false),
    [drivers]
  );

  const sites = React.useMemo(() => {
    const p = parties.find((x) => x.id === partyId);
    return p?.sites || [];
  }, [parties, partyId]);

  React.useEffect(() => {
    setValue("site_id", "");
    // clear stale grade selections when party changes
    (rows || []).forEach((row, idx) => {
      const gid = Number(row?.grade_id);
      if (!gid) return;
      if (!grades.some((g) => g.id === gid)) {
        setValue(`rows.${idx}.grade_id`, "");
        setValue(`rows.${idx}.rate`, "");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyId]);

  const applyRateForRow = async (index, gradeId) => {
    if (!partyId || !gradeId) return;
    const info = await fetchRateDetails(partyId, gradeId, watch("date"));
    const r = info?.rate ?? null;
    if (r != null) setValue(`rows.${index}.rate`, String(r));
  };

  const saveBulk = useMutation({
    mutationFn: (data) => {
      const payload = {
        party_id: Number(data.party_id),
        date: data.date,
        site_id: Number(data.site_id),
        order_by: data.order_by || "",
        rows: data.rows.map((r) => {
          const qty = Number(r.qty_cum);
          const rate = Number(r.rate);
          const explicitAmt = Number(r.amount);
          const amount = Number.isFinite(explicitAmt) && explicitAmt > 0 ? explicitAmt : qty * rate;
          const drv = drivers.find((d) => Number(d.id) === Number(r.driver_id));
          return {
            vehicle_id: Number(r.vehicle_id),
            driver_name: drv?.name || "",
            grade_id: Number(r.grade_id),
            batch_no: r.batch_no || "",
            qty: roundToDecimals(qty, 3).toFixed(3),
            rate: roundMoney(rate).toFixed(2),
            amount: roundMoney(amount).toFixed(2),
            remark: r.remark || "",
          };
        }),
      };
      return rmcClient.createSalesChallansBulk(payload);
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["salesChallans"] });
      qc.invalidateQueries({ queryKey: ["nextSalesChallanNumber"] });
      toast.success("Bulk challans created");
      const payload = res?.data || res;
      const idsFromArray = Array.isArray(payload)
        ? payload.map((x) => Number(x?.id ?? x)).filter((n) => Number.isFinite(n) && n > 0)
        : [];
      const idsFromKeys = [
        ...(Array.isArray(payload?.challan_ids) ? payload.challan_ids : []),
        ...(Array.isArray(payload?.ids) ? payload.ids : []),
        ...(Array.isArray(payload?.created_ids) ? payload.created_ids : []),
        ...(Array.isArray(payload?.results) ? payload.results.map((x) => x?.id) : []),
      ]
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n) && n > 0);
      const challanIds = Array.from(new Set([...idsFromArray, ...idsFromKeys]));
      if (challanIds.length) {
        setCreatedChallanIds(challanIds);
        setInvoicePrompt(true);
      } else {
        toast("Bulk created. Generate invoices from Sales > Invoices if needed.", { icon: "ℹ️" });
      }
      reset({
        party_id: "",
        date: new Date().toISOString().slice(0, 10),
        site_id: "",
        order_by: "",
        rows: [{ ...emptyRow }],
      });
    },
    onError: (e) => toast.error(e?.response?.data?.message || "Failed to create bulk challans"),
  });

  const createBulkInvoices = useMutation({
    mutationFn: async ({ challanIds, taxType }) => {
      const res = await rmcClient.createSalesInvoicesBulk({
        challan_ids: challanIds,
        tax_type: taxType,
      });
      const failMsg = extractApiEnvelopeMessage(res?.data);
      if (failMsg) throw new Error(failMsg);
      return res;
    },
    onSuccess: () => {
      toast.success("Bulk invoices generated");
      setInvoicePrompt(false);
      setCreatedChallanIds([]);
      qc.invalidateQueries({ queryKey: ["salesInvoices"] });
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-800 bg-gray-950/30 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
          Common Details
        </p>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <Label>Date</Label>
            <Controller
              name="date"
              control={control}
              render={({ field }) => <Input type="date" className="mt-1" {...field} value={field.value || ""} />}
            />
          </div>
          <Controller
            name="party_id"
            control={control}
            render={({ field }) => (
              <SearchableSelect
                label="Party"
                value={field.value}
                onChange={field.onChange}
                options={parties}
                getSubLabel={(p) => p.gst_number}
                overlayZClass="z-[100001]"
                menuZClass="z-[100002]"
              />
            )}
          />
          <Controller
            name="site_id"
            control={control}
            render={({ field }) => (
              <SearchableSelect
                label="Site"
                value={field.value}
                onChange={field.onChange}
                options={sites}
                getLabel={(s) => s.name}
                getValue={(s) => s.id}
                disabled={!partyId}
              />
            )}
          />
          <div>
            <Label>Order By</Label>
            <Controller
              name="order_by"
              control={control}
              render={({ field }) => <Input className="mt-1" placeholder="Optional" {...field} value={field.value || ""} />}
            />
          </div>
        </div>
        {(errors.party_id || errors.site_id || errors.date) && (
          <p className="mt-2 text-[11px] text-red-400">
            {errors.party_id?.message || errors.site_id?.message || errors.date?.message}
          </p>
        )}
      </div>

      <div className="rounded-xl border border-gray-800 bg-gray-950/30 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Bulk Rows</p>
        <div className="overflow-x-auto rounded-lg border border-gray-800">
          <table className="min-w-[1200px] w-full text-xs">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-950/80">
                <th className="px-2 py-2 text-left text-gray-500">Vehicle</th>
                <th className="px-2 py-2 text-left text-gray-500">Driver</th>
                <th className="px-2 py-2 text-left text-gray-500">Grade</th>
                <th className="px-2 py-2 text-left text-gray-500">Batch No</th>
                <th className="px-2 py-2 text-left text-gray-500">QTY</th>
                <th className="px-2 py-2 text-left text-gray-500">Rate</th>
                <th className="px-2 py-2 text-left text-gray-500">Amount</th>
                <th className="px-2 py-2 text-left text-gray-500">Remark</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {fields.map((f, index) => {
                const qty = Number(rows?.[index]?.qty_cum || 0);
                const rate = Number(rows?.[index]?.rate || 0);
                const calcAmount = qty * rate;
                return (
                  <tr key={f.id} className="border-b border-gray-800/80">
                    <td className="px-2 py-2 min-w-[200px]">
                      <Controller
                        name={`rows.${index}.vehicle_id`}
                        control={control}
                        render={({ field }) => (
                          <SearchableSelect
                            value={field.value}
                            onChange={(v) => {
                              field.onChange(v);
                              setValue(`rows.${index}.driver_id`, "");
                            }}
                            options={vehicles}
                            getLabel={(v) =>
                              `${v.registration_no}${v.name || v.alias ? ` (${v.name || v.alias})` : ""}`
                            }
                            getValue={(v) => v.id}
                            overlayZClass="z-[100001]"
                            menuZClass="z-[100002]"
                          />
                        )}
                      />
                    </td>
                    <td className="px-2 py-2 min-w-[220px]">
                      <Controller
                        name={`rows.${index}.driver_id`}
                        control={control}
                        render={({ field }) => (
                          <SearchableSelect
                            value={field.value}
                            onChange={field.onChange}
                            options={driverOptions}
                            getLabel={(d) =>
                              d.name
                                ? `${d.name}${d.contact_no ? ` · ${d.contact_no}` : ""}${d.employee_id ? ` (${d.employee_id})` : ""}`
                                : `Driver #${d.id}`
                            }
                            getValue={(d) => d.id}
                            overlayZClass="z-[100001]"
                            menuZClass="z-[100002]"
                          />
                        )}
                      />
                    </td>
                    <td className="px-2 py-2 min-w-[180px]">
                      <Controller
                        name={`rows.${index}.grade_id`}
                        control={control}
                        render={({ field }) => (
                          <SearchableSelect
                            value={field.value}
                            onChange={(v) => {
                              field.onChange(v);
                              const gid = v ? Number(v) : null;
                              if (gid) applyRateForRow(index, gid);
                            }}
                            options={grades}
                            getLabel={(g) => g.name}
                            getValue={(g) => g.id}
                            disabled={!partyId}
                            overlayZClass="z-[100001]"
                            menuZClass="z-[100002]"
                          />
                        )}
                      />
                    </td>
                    <td className="px-2 py-2 min-w-[140px]">
                      <Controller
                        name={`rows.${index}.batch_no`}
                        control={control}
                        render={({ field }) => <Input {...field} value={field.value || ""} />}
                      />
                    </td>
                    <td className="px-2 py-2 min-w-[110px]">
                      <Controller
                        name={`rows.${index}.qty_cum`}
                        control={control}
                        render={({ field }) => (
                          <Input type="number" step="0.001" {...field} value={field.value || ""} />
                        )}
                      />
                    </td>
                    <td className="px-2 py-2 min-w-[120px]">
                      <Controller
                        name={`rows.${index}.rate`}
                        control={control}
                        render={({ field }) => (
                          <Input type="number" step="0.01" {...field} value={field.value || ""} />
                        )}
                      />
                    </td>
                    <td className="px-2 py-2 min-w-[140px]">
                      <Controller
                        name={`rows.${index}.amount`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="number"
                            step="0.01"
                            placeholder={Number.isFinite(calcAmount) ? roundMoney(calcAmount).toFixed(2) : "Auto"}
                            {...field}
                            value={field.value || ""}
                          />
                        )}
                      />
                    </td>
                    <td className="px-2 py-2 min-w-[180px]">
                      <Controller
                        name={`rows.${index}.remark`}
                        control={control}
                        render={({ field }) => <Input {...field} value={field.value || ""} />}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => remove(index)}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {errors.rows?.message && <p className="mt-2 text-[11px] text-red-400">{errors.rows.message}</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => append({ ...emptyRow })}
          >
            Add Row
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSubmit((d) => saveBulk.mutate(d))}
            disabled={saveBulk.isPending}
          >
            {saveBulk.isPending ? "Creating..." : "Create Bulk Challans"}
          </Button>
        </div>
      </div>

      <Modal
        isOpen={invoicePrompt}
        onClose={() => {
          if (!createBulkInvoices.isPending) {
            setInvoicePrompt(false);
            setCreatedChallanIds([]);
          }
        }}
        className="max-w-md w-full"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Generate Invoices for Bulk Challans?</h3>
          <p className="text-sm text-gray-400">
            {createdChallanIds.length} challans created. Choose invoice type preference to continue.
          </p>
          <div className="flex flex-wrap justify-end gap-2 border-t border-gray-800 pt-4">
            <Button
              size="sm"
              variant="outline"
              disabled={createBulkInvoices.isPending}
              onClick={() => {
                setInvoicePrompt(false);
                setCreatedChallanIds([]);
              }}
            >
              Skip
            </Button>
          </div>
          <div className="rounded-lg border border-gray-800 bg-gray-950/30 p-3">
            <p className="mb-2 text-xs text-gray-400">Invoice Tax Type</p>
            <div className="flex items-center gap-6">
              <Radio
                id="bulk-invoice-gst"
                name="bulk-invoice-tax"
                value="GST"
                label="GST"
                checked={invoiceTaxType === "GST"}
                onChange={() => setInvoiceTaxType("GST")}
              />
              <Radio
                id="bulk-invoice-non-gst"
                name="bulk-invoice-tax"
                value="NON_GST"
                label="Non-GST"
                checked={invoiceTaxType === "NON_GST"}
                onChange={() => setInvoiceTaxType("NON_GST")}
              />
            </div>
          </div>
          <div className="flex justify-end border-t border-gray-800 pt-3">
            <Button
              size="sm"
              disabled={createBulkInvoices.isPending || !createdChallanIds.length}
              onClick={() =>
                createBulkInvoices.mutate({
                  challanIds: createdChallanIds,
                  taxType: invoiceTaxType,
                })
              }
            >
              {createBulkInvoices.isPending ? "Generating..." : "Generate Invoices"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
